import { useRef, useState } from 'react';
import {
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from '@mui/icons-material';
import { Box, Button, IconButton } from '@mui/material';

export const Carousel = ({ children, disableButtonsInMobile = false, ...props }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [showNavigationButton, setShowNavigationButton] = useState(false);
  const maxSteps = children.length;
  const scrollRef = useRef(null);

  const handleNext = () => {
    const step = activeStep + 1;
    setActiveStep(step);
    moveCarousel(step);
  };

  const handleBack = () => {
    const step = activeStep - 1;
    setActiveStep(step);
    moveCarousel(step);
  };

  const handleDotClick = (index) => {
    setActiveStep(index);
    moveCarousel(index);
  };

  const moveCarousel = (step) => {
    const container = scrollRef.current;
    const childWidth = container?.clientWidth || 0;
    container?.scrollTo({
      left: childWidth * step,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <Box
        sx={{ position: 'relative' }}
        onMouseOver={() => setShowNavigationButton(true)}
        onMouseLeave={() => setShowNavigationButton(false)}
        onFocus={() => setShowNavigationButton(true)}
        onBlur={() => setShowNavigationButton(false)}
      >
        <Box
          ref={scrollRef}
          pb={1}
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'stretch',
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
          {...props}
          onScroll={(e) => {
            const scrollLeft = e.target.scrollLeft;
            const width = e.target.clientWidth;
            const index = Math.round(scrollLeft / width);
            if (index !== activeStep) {
              setActiveStep(index);
            }
          }}
        >
          {children.map((child, index) => (
            <Box
              key={index}
              sx={{
                flexShrink: 0,
                width: '100%',
                scrollSnapAlign: 'start',
              }}
            >
              {child}
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: '0',
            left: '0',
            bottom: '0',
            width: '55px',
            display: { xs: disableButtonsInMobile ? 'none' : 'flex', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Button
            variant='contained'
            disableElevation
            size='small'
            onClick={handleBack}
            disabled={activeStep === 0}
            style={{
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              minWidth: 'unset',
              minHeight: 'unset',
              opacity: showNavigationButton ? 1 : 0,
              transition: 'opacity 0.3s ease, transform 0.3s ease',
              pointerEvents: showNavigationButton ? 'auto' : 'none',
            }}
          >
            <KeyboardArrowLeftIcon />
          </Button>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: '0',
            right: '0',
            bottom: '0',
            width: '55px',
            display: { xs: disableButtonsInMobile ? 'none' : 'flex', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Button
            variant='contained'
            disableElevation
            size='small'
            onClick={handleNext}
            disabled={activeStep === maxSteps - 1}
            style={{
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              minWidth: 'unset',
              minHeight: 'unset',
              opacity: showNavigationButton ? 1 : 0,
              transition: 'opacity 0.3s ease, transform 0.3s ease',
              pointerEvents: showNavigationButton ? 'auto' : 'none',
            }}
          >
            <KeyboardArrowRightIcon />
          </Button>
        </Box>
      </Box>
      <Box display='flex' justifyContent='center' mt={1}>
        {[...Array(maxSteps)].map((_, index) => (
          <IconButton
            key={index}
            onClick={() => handleDotClick(index)}
            sx={{
              p: 0.5,
              mx: 0.5,
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: index === activeStep ? 'primary.main' : 'grey.400',
              transition: 'all 0.3s',
              '&:hover': {
                backgroundColor: index === activeStep ? 'primary.dark' : 'grey.500',
              },
            }}
          />
        ))}
      </Box>
    </>
  );
};
